import React, { useState, useEffect } from "react";
import styles from "./CalendarSection.module.css";
import commonStyles from "components/commonStyle/commonStyle.module.css";
import { WorkTimeSlot, HolidaySlot } from "modules/calendar-worktime-holiday";
import { fetchWorkSlots, fetchHolidaySlots } from "services/workTime/calendar-service";
import { getWeekdayLetter, isPastDateTime, isSameDate, uniqueTimeSlots, updateWeeks } from "utils/calender";
import CalendarControler from "components/calender/calenderController";
import { getSlotBookingDateCounter } from "services/booking/get-slot-booking-date-counter";


export default function CalendarSection() {

  const [workTimeSlots, setWorkTimeSlots] = useState<WorkTimeSlot[]>([]);
  const [holidaySlots, setHolidaySlots] = useState<HolidaySlot[]>([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [week, setWeek] = useState<Date[]>([]);
  const [bookingData, setBookingData] = useState<any>({});
  const currentTime = new Date();
  const times = uniqueTimeSlots(workTimeSlots, week);

  const fetchData = async () => {
    try {
      const workData = await fetchWorkSlots();
      const holidayData = await fetchHolidaySlots();
      setWorkTimeSlots(workData);
      setHolidaySlots(holidayData);
    } catch (error) {
      console.error("Fehler beim Laden der Daten:", error);
    }
  };

  const fetchBookingCounter = async () => {
    try {
      if (week.length > 1) {
        const startDate = week[0];
        const endDate = week[week.length - 1];
        const result = await getSlotBookingDateCounter(startDate, endDate);
        setBookingData(result.data);
      } else {
        console.error("Das Wochen-Array ist nicht richtig initialisiert.");
      }
    } catch (error) {
      console.error("Fehler beim Laden der Buchungenszähler:", error);
    }
  };

  useEffect(() => {
    setWeek(updateWeeks(0, 2));
    fetchData();
  }, []);

  useEffect(() => {
    if (week && week.length > 1) {
      fetchBookingCounter();
    }
  }, [week]);

  const handleCurrentWeek = () => {
    setWeekOffset(0);
    setWeek(updateWeeks(0, 2))
  };

  const handleNextWeek = () => {
    setWeekOffset((prevOffset) => {
      const newOffset = prevOffset + 2;
      setWeek(updateWeeks(newOffset, 2))
      return newOffset;
    });
  };

  const handlePreviousWeek = () => {
    setWeekOffset((prevOffset) => {
      if (prevOffset > 0) {
        const newOffset = prevOffset - 2;
        setWeek(updateWeeks(newOffset, 2))
        return newOffset;
      }
      return prevOffset;
    });
  };

  return (
    <div className={`${commonStyles.sectionContainer} ${styles.container}`}>
      <div className={`${commonStyles.sectionTitle} ${styles.kalenderTitle}`}>
        <h1>Übersicht verfügbarer Flexi Plätze</h1>
        <div className={`${commonStyles.sectionBorderBottom} ${styles.borderBottom}`}></div>
      </div>
      <div className={styles.CalendarController}>
        <CalendarControler
          currentDate={currentTime}
          week={week}
          weekOffset={weekOffset}
          onCurrentWeek={handleCurrentWeek}
          onPreviousWeek={handlePreviousWeek}
          onNextWeek={handleNextWeek}
          showPrevious={false} />
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Uhrzeit</th>
            {week.slice(0, 5).map((day, index) => (
              <th key={index}>{getWeekdayLetter(day).slice(0, 2)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[[0, 5], [5, 10]].map((weekRange, weekIndex) => (
            <React.Fragment key={`week-${weekIndex}`}>
              <tr className={styles.kalenderDatum}>
                <td className={styles.kalenderUhrzeit}></td>
                {week.slice(weekRange[0], weekRange[1]).map((day, index) => (
                  <td key={index} className={day < currentTime ? styles.past : ""}>
                    <div className={isSameDate(day, currentTime) ? styles.currentDay : ""}>
                      {day.getDate().toString().padStart(2, '0')}
                    </div>
                  </td>
                ))}
              </tr>

              {times.map((time, index) => (
                <tr key={`${index}-week${weekIndex + 1}`}>
                  <td className={styles.kalenderUhrzeit}>{time}</td>
                  {week.slice(weekRange[0], weekRange[1]).map((day, dayIndex) => {

                    const holidaysInDay = holidaySlots.filter((holidaySlot) => {
                      const holidayDate = new Date(holidaySlot.date);
                      return isSameDate(day, holidayDate);
                    });

                    const isFullClosed = holidaysInDay.some(holiday => holiday.is_full_day) ||
                      holidaysInDay.length === workTimeSlots.filter((slot) => {
                        return slot.day_of_week === getWeekdayLetter(day);
                      }).length
                    if (isFullClosed) {
                      return index === 0 ? (
                        <td key={dayIndex} rowSpan={times.length} className={styles.past}>
                          <center>
                            <span className={styles.geschlossen}>geschlossen</span>
                          </center>
                        </td>
                      ) : (null);
                    } else {
                      const slot = workTimeSlots.find((slot) =>
                        slot.day_of_week === getWeekdayLetter(day) &&
                        slot.start_time.substring(0, 5) === time.split(" - ")[0] &&
                        slot.end_time.substring(0, 5) === time.split(" - ")[1]
                      );
                      const placesCount = slot ? Number(slot.number_of_places) : 0;
                      const isPastDate = isPastDateTime(time, currentTime, day)
                      const isClosed = !slot || holidaysInDay.some(holiday => holiday.block === slot.id);

                      const dateStr = day.toISOString().split('T')[0];
                      const bookingsForDate = bookingData[dateStr] || [];
                      const confirmedBookings = bookingsForDate.find(
                        (booking: any) => booking.work_time_slot_id.toString() === slot?.id
                      );
                      const bookedPlaces = confirmedBookings ? confirmedBookings.confirmed_bookings_count : 0;
                      const placesLeft = (placesCount - bookedPlaces) > 0 ? placesCount - bookedPlaces : 0;
                      return (
                        <td key={dayIndex} className={isPastDate || placesLeft === 0 || isClosed ? styles.past : ""}>
                          {isClosed ? (
                            <span className={styles.geschlossen}>geschlossen</span>
                          ) : (
                            <div className={styles.platzContainer}>
                              <span className={isPastDate || placesLeft === 0 || isClosed ? styles.platzNumbedisble : styles.platzNumber}>
                                {placesLeft}
                              </span>
                              <span className={styles.platzLebel}>Plätze</span>
                            </div>
                          )}
                        </td>
                      );
                    }
                  })}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>

    </div>
  );
}

